import React, { useState } from 'react';

import { ListHookResult } from '_lib/api/apiResourceFactory';
import { Location, ServiceTitanJobHistoryItem, TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';

import { Accordion } from '_lib/components/Accordion/Accordion';
import { AccordionDetails } from '_lib/components/AccordionDetail/AccordionDetail';
import { AccordionSummary } from '_lib/components/AccordionSummary/AccordionSummary';
import { Box } from '_lib/components/Box/Box';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { PagedList } from '_lib/featureComponents/PagedList/PagedList';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { formatAddressOneLine, formatAddressTwoLine } from '_lib/utils/helpers';

import { AppointmentList } from '../AppointmentList/AppointmentList';

export type JobListProps = {
  companyId: string;
  customerId: string;
  companyTimezone: TimezoneAbbreviationEnum;
  jobHistoryQueryResult: ListHookResult<ServiceTitanJobHistoryItem>;
  locations?: Location[];
  selectedLocation?: Location;
};

const allLocationsId = 'ALL';

export const JobList = (props: JobListProps): React.ReactElement => {
  const { companyId, customerId, companyTimezone, jobHistoryQueryResult, locations, selectedLocation } = props;

  const [filterLocationId, setFilterLocationId] = useState<string>(selectedLocation?.providerId || allLocationsId);

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  return (
    <Box>
      <Box sx={{ paddingBottom: 1 }}>
        <TextField
          fullWidth
          label='Filter by Location'
          value={filterLocationId}
          disabled={!locations || locations.length <= 1}
          select
          onChange={(e) => {
            setFilterLocationId(e.target.value);
          }}>
          <MenuItem key={'location-all'} value={allLocationsId}>
            <Typography sx={{ whiteSpace: 'pre-line' }} text='Every Location' />
          </MenuItem>
          {locations &&
            locations.map((location: Location) => (
              <MenuItem key={`location-${location.providerId}`} value={location.providerId}>
                <Typography
                  sx={{ whiteSpace: 'pre-line' }}
                  text={`${location.name} - ${formatAddressOneLine(location)}`}
                />
              </MenuItem>
            ))}
        </TextField>
      </Box>
      <PagedList
        queryResult={jobHistoryQueryResult}
        emptyListMessage='No job history for selected location.'
        filterFunction={(job) =>
          filterLocationId === allLocationsId ? true : job.providerLocationId === filterLocationId
        }
        renderFunction={(job) => {
          const locationForJob = locations?.find((l) => l.providerId === job.providerLocationId);

          return (
            <Accordion key={job.providerId} expanded={selectedJobId === job.providerId}>
              <AccordionSummary
                onClick={() =>
                  selectedJobId === job.providerId ? setSelectedJobId(null) : setSelectedJobId(job.providerId)
                }>
                <Box>
                  <Typography variant='body1Bold' text={`Job # ${job?.jobNo} - ${job.status}`} />
                  {job.firstAppointment && (
                    <Typography
                      text={`First Appointment: ${DateUtils.format(
                        job.firstAppointment.start,
                        DateFormatEnum.DateTimeWeekday,
                        { timezone: companyTimezone, showTimezone: true }
                      )}`}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {locationForJob && (
                  <Typography
                    component='p'
                    text={formatAddressTwoLine(locationForJob)}
                    sx={{ whiteSpace: 'pre-line' }}
                  />
                )}
                <Typography text={`Job #: ${job.jobNo}`} />
                <Typography text={`Priority: ${job.priority}`} />
                <Typography text={`Summary: ${job.notes || 'No summary'}`} />
                {selectedJobId === job.providerId && (
                  <AppointmentList
                    companyId={companyId}
                    customerId={customerId}
                    providerJobId={job.providerId}
                    companyTimezone={companyTimezone}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          );
        }}
      />
    </Box>
  );
};
